import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  Tag,
  Layers,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { menuAPI } from '../lib/api';
import { toast } from 'sonner';

const emptyGroup = () => ({
  name: '',
  description: '',
  required: false,
  min_selections: 0,
  max_selections: 1,
  options: [],
  item_ids: [],
  category_ids: [],
});

export const ModifiersPage = () => {
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyGroup());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, c, i] = await Promise.all([
        menuAPI.getModifiers(),
        menuAPI.getCategories(),
        menuAPI.getItems(),
      ]);
      setGroups(g.data || []);
      setCategories(c.data || []);
      setItems(i.data || []);
    } catch (e) {
      toast.error('Kon add-ons niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyGroup());
    setShowDialog(true);
  };

  const openEdit = (group) => {
    setEditing(group);
    setForm({
      name: group.name || '',
      description: group.description || '',
      required: !!group.required,
      min_selections: group.min_selections || 0,
      max_selections: group.max_selections || 1,
      options: group.options || [],
      item_ids: group.item_ids || [],
      category_ids: group.category_ids || [],
    });
    setShowDialog(true);
  };

  const addOption = () => {
    setForm(f => ({
      ...f,
      options: [...f.options, { name: '', price_delta: 0, is_default: false, sort_order: f.options.length }]
    }));
  };

  const updateOption = (idx, field, value) => {
    setForm(f => ({
      ...f,
      options: f.options.map((o, i) => i === idx ? { ...o, [field]: value } : o)
    }));
  };

  const removeOption = (idx) => {
    setForm(f => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));
  };

  const toggleItemLink = (itemId) => {
    setForm(f => ({
      ...f,
      item_ids: f.item_ids.includes(itemId) ? f.item_ids.filter(x => x !== itemId) : [...f.item_ids, itemId]
    }));
  };

  const toggleCategoryLink = (catId) => {
    setForm(f => ({
      ...f,
      category_ids: f.category_ids.includes(catId) ? f.category_ids.filter(x => x !== catId) : [...f.category_ids, catId]
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Geef een naam op'); return; }
    if (form.options.length === 0) { toast.error('Voeg minimaal 1 optie toe'); return; }
    if (form.item_ids.length === 0 && form.category_ids.length === 0) {
      toast.error('Koppel deze add-on aan tenminste 1 gerecht of categorie');
      return;
    }
    const payload = {
      ...form,
      min_selections: form.required ? Math.max(1, form.min_selections) : 0,
      options: form.options.filter(o => o.name.trim()),
    };
    try {
      if (editing) {
        await menuAPI.updateModifier(editing.id, payload);
        toast.success('Add-on bijgewerkt');
      } else {
        await menuAPI.createModifier(payload);
        toast.success('Add-on aangemaakt');
      }
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error('Opslaan mislukt');
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Verwijder "${group.name}"?`)) return;
    try {
      await menuAPI.deleteModifier(group.id);
      toast.success('Add-on verwijderd');
      load();
    } catch (e) {
      toast.error('Verwijderen mislukt');
    }
  };

  return (
    <div className="space-y-6" data-testid="modifiers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Add-ons & Toppings</h1>
          <p className="text-gray-500">Extra opties die klanten kunnen kiezen bij gerechten (saus, kaas, extra&apos;s…)</p>
        </div>
        <Button onClick={openCreate} className="btn-primary" data-testid="create-modifier-btn">
          <Plus className="w-4 h-4 mr-2" /> Nieuwe Add-on Groep
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500 mx-auto" /></div>
      ) : groups.length === 0 ? (
        <div className="card-dashboard text-center py-16">
          <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-heading text-lg font-semibold text-gray-700 mb-2">Nog geen add-ons</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Voeg add-on groepen toe zoals &ldquo;Saus keuze&rdquo;, &ldquo;Extra toppings&rdquo; of &ldquo;Frites grootte&rdquo; en koppel ze aan gerechten of hele categorieën.
          </p>
          <Button onClick={openCreate} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" /> Maak je eerste add-on groep
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {groups.map(g => (
            <div key={g.id} className="card-dashboard" data-testid={`modifier-group-${g.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
                    {g.name}
                    {g.required && (
                      <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-medium">
                        Verplicht
                      </span>
                    )}
                  </h3>
                  {g.description && <p className="text-sm text-gray-500 mt-1">{g.description}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(g)} className="p-2 text-gray-400 hover:text-[#1A4D2E]" data-testid={`edit-modifier-${g.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(g)} className="p-2 text-gray-400 hover:text-red-500" data-testid={`delete-modifier-${g.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-500 mb-3">
                {g.max_selections === 1 ? 'Single choice' : `Multi (max ${g.max_selections})`}
                {g.min_selections > 0 && ` · min ${g.min_selections}`}
              </div>

              <div className="space-y-1.5 mb-3">
                {(g.options || []).slice(0, 5).map((o, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-gray-700">{o.name}{o.is_default && <span className="ml-2 text-xs text-emerald-600">standaard</span>}</span>
                    {o.price_delta > 0 && <span className="text-gray-500 font-mono text-xs">+€{Number(o.price_delta).toFixed(2)}</span>}
                  </div>
                ))}
                {(g.options || []).length > 5 && (
                  <div className="text-xs text-gray-400">…{g.options.length - 5} meer</div>
                )}
              </div>

              <div className="text-xs text-gray-500 border-t pt-3">
                Gekoppeld aan:&nbsp;
                {g.category_ids?.length > 0 && (
                  <span className="text-emerald-700 font-medium">{g.category_ids.length} categorieën</span>
                )}
                {g.category_ids?.length > 0 && g.item_ids?.length > 0 && ' · '}
                {g.item_ids?.length > 0 && (
                  <span className="text-emerald-700 font-medium">{g.item_ids.length} gerechten</span>
                )}
                {(g.category_ids?.length || 0) + (g.item_ids?.length || 0) === 0 && (
                  <span className="text-rose-500 inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> niet gekoppeld
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Add-on bewerken' : 'Nieuwe add-on groep'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Naam *</label>
              <Input
                placeholder="bv. Saus keuze, Extra toppings, Frites grootte"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="modifier-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Beschrijving</label>
              <Textarea
                placeholder="Optionele uitleg voor de klant"
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Verplicht</p>
                  <p className="text-xs text-gray-500">Klant moet kiezen</p>
                </div>
                <Switch
                  checked={form.required}
                  onCheckedChange={(v) => setForm(f => ({ ...f, required: v, min_selections: v ? Math.max(1, f.min_selections) : 0 }))}
                  data-testid="modifier-required-switch"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Max selecties</p>
                  <p className="text-xs text-gray-500">1 = single choice</p>
                </div>
                <Input
                  type="number"
                  min="1"
                  className="w-16"
                  value={form.max_selections}
                  onChange={(e) => setForm(f => ({ ...f, max_selections: Math.max(1, parseInt(e.target.value) || 1) }))}
                  data-testid="modifier-max-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Opties</label>
                <Button variant="outline" size="sm" onClick={addOption} data-testid="add-option-btn">
                  <Plus className="w-4 h-4 mr-1" /> Optie toevoegen
                </Button>
              </div>
              <div className="space-y-2">
                {form.options.map((o, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`option-row-${idx}`}>
                    <Input
                      className="col-span-6"
                      placeholder="Optienaam (bv. Mayonaise)"
                      value={o.name}
                      onChange={(e) => updateOption(idx, 'name', e.target.value)}
                    />
                    <div className="col-span-3 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">+€</span>
                      <Input
                        type="number"
                        step="0.10"
                        className="pl-7"
                        placeholder="0.00"
                        value={o.price_delta}
                        onChange={(e) => updateOption(idx, 'price_delta', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <label className="col-span-2 flex items-center gap-1 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={!!o.is_default}
                        onChange={(e) => updateOption(idx, 'is_default', e.target.checked)}
                      /> Standaard
                    </label>
                    <button
                      onClick={() => removeOption(idx)}
                      className="col-span-1 text-gray-400 hover:text-red-500"
                      title="Verwijderen"
                    >
                      <X className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                ))}
                {form.options.length === 0 && (
                  <p className="text-sm text-gray-400 italic text-center py-3">Voeg minimaal 1 optie toe</p>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                <Layers className="w-4 h-4" /> Koppel aan categorieën (geldt voor alle items in deze categorie)
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.map(c => (
                  <button
                    key={c.id}
                    onClick={() => toggleCategoryLink(c.id)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition ${
                      form.category_ids.includes(c.id)
                        ? 'bg-emerald-500 text-white border-emerald-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-500'
                    }`}
                    data-testid={`link-cat-${c.id}`}
                  >
                    {c.name}
                  </button>
                ))}
                {categories.length === 0 && (
                  <p className="text-sm text-gray-400 italic">Geen categorieën aangemaakt</p>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                <Tag className="w-4 h-4" /> Of koppel aan specifieke gerechten
              </label>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
                {items.map(it => (
                  <button
                    key={it.id}
                    onClick={() => toggleItemLink(it.id)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition ${
                      form.item_ids.includes(it.id)
                        ? 'bg-emerald-500 text-white border-emerald-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-500'
                    }`}
                    data-testid={`link-item-${it.id}`}
                  >
                    {it.name}
                  </button>
                ))}
                {items.length === 0 && (
                  <p className="text-sm text-gray-400 italic">Geen gerechten aangemaakt</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuleren</Button>
            <Button className="btn-primary" onClick={handleSave} data-testid="save-modifier-btn">
              {editing ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
